import {
    Box,
    Flex,
    HStack,
    Icon,
    Image,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Portal,
    SimpleGrid,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { Spinner } from 'components/v4';
import { dashboardTokens } from '../dashboardTokens';

const SUMMARY_TILES = [
    { key: 'services', label: 'Services' },
    { key: 'numbers', label: 'Numbers' },
    { key: 'users', label: 'Users' },
    { key: 'trunks', label: 'Trunks' },
];

const BREAKDOWN_ICON_SIZE = '14px';

// The section icons draw a fixed 25px image of their own (some take no size),
// so the slot overrides whatever is inside it.
const iconSlotSx = {
    '& img, & svg': {
        width: `${BREAKDOWN_ICON_SIZE} !important`,
        height: `${BREAKDOWN_ICON_SIZE} !important`,
    },
};

/** A tile's count split into groups, one row each: icon, name and count. */
const Breakdown = ({ items, darkMode }) => (
    <VStack as="ul" align="stretch" spacing={1.5} m={0} listStyleType="none">
        {items.map(({ title, icon, image, count }) => (
            <HStack
                as="li"
                key={title}
                spacing={2}
                fontSize="xs"
                color={darkMode ? 'gray.100' : 'gray.800'}>
                {image ? (
                    <Image
                        src={image}
                        alt=""
                        boxSize={BREAKDOWN_ICON_SIZE}
                        objectFit="contain"
                        flexShrink={0}
                    />
                ) : icon ? (
                    <Flex
                        boxSize={BREAKDOWN_ICON_SIZE}
                        flexShrink={0}
                        align="center"
                        justify="center"
                        sx={iconSlotSx}>
                        <Icon as={icon} boxSize={BREAKDOWN_ICON_SIZE} />
                    </Flex>
                ) : (
                    <Box boxSize={BREAKDOWN_ICON_SIZE} flexShrink={0} />
                )}
                <Text flex="1" minW={0} noOfLines={1} marginBottom={0}>
                    {title}
                </Text>
                <Text fontWeight="semibold" marginBottom={0}>
                    {count}
                </Text>
            </HStack>
        ))}
    </VStack>
);

/** The breakdown, or why there isn't one yet. */
const BreakdownContent = ({ breakdown, darkMode }) => {
    const muted = darkMode ? 'gray.400' : 'gray.500';

    if (breakdown.isLoading) {
        return (
            <Flex justify="center" py={2}>
                <Spinner size="sm" color={muted} />
            </Flex>
        );
    }

    return (
        <>
            {breakdown.items.length > 0 ? (
                <Breakdown items={breakdown.items} darkMode={darkMode} />
            ) : (
                <Text fontSize="xs" color={muted} marginBottom={0}>
                    {breakdown.emptyText}
                </Text>
            )}
            {breakdown.note && breakdown.items.length > 0 && (
                <Text fontSize="10px" mt={2} color={muted} marginBottom={0}>
                    {breakdown.note}
                </Text>
            )}
        </>
    );
};

/**
 * (3133) Headline counts: blue once there is at least one, grey at zero. A
 * count that couldn't be loaded shows "-". Hovering a tile that has a
 * breakdown shows its count split by group.
 *
 * @param {object} props
 * @param {Record<string, ?number>} props.counts - keyed like SUMMARY_TILES
 * @param {Record<string, import('./useAccountSummaryCounts').SummaryBreakdown>} [props.breakdowns]
 *   - keyed like SUMMARY_TILES
 */
const SummaryTiles = ({ counts, breakdowns = {} }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const { tile } = dashboardTokens(darkMode);

    return (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mt={3}>
            {SUMMARY_TILES.map(({ key, label }) => {
                const value = counts[key];
                const colors = value > 0 ? tile.filled : tile.empty;
                const breakdown = value > 0 ? breakdowns[key] : null;
                // One loaded on first view has nothing to list until then.
                const hasBreakdown =
                    breakdown?.items?.length > 0 || !!breakdown?.onOpen;

                const summaryTile = (
                    <Box
                        key={key}
                        role="group"
                        aria-label={`${label}: ${value ?? 'unknown'}`}
                        tabIndex={hasBreakdown ? 0 : undefined}
                        borderRadius="lg"
                        borderWidth="1px"
                        p={3}
                        textAlign="center"
                        bg={colors.bg}
                        borderColor={colors.border}>
                        <Text
                            fontSize="xl"
                            fontWeight="bold"
                            lineHeight="short"
                            color={colors.value}>
                            {value ?? '-'}
                        </Text>
                        <Text fontSize="11px" mt={0.5} color={colors.label}>
                            {label}
                        </Text>
                    </Box>
                );

                if (!hasBreakdown) return summaryTile;

                return (
                    <Popover
                        key={key}
                        trigger="hover"
                        placement="bottom-start"
                        strategy="fixed"
                        openDelay={100}
                        onOpen={breakdown.onOpen}
                        isLazy>
                        <PopoverTrigger>{summaryTile}</PopoverTrigger>
                        <Portal>
                            <PopoverContent
                                rootProps={{ zIndex: 'tooltip' }}
                                aria-label={`${label} breakdown`}
                                w="220px"
                                maxW="calc(100vw - 16px)"
                                px={3}
                                py={2}
                                borderRadius="lg"
                                boxShadow="lg"
                                bg={darkMode ? '#1f1f1f' : 'white'}
                                borderColor={darkMode ? '#374151' : 'gray.200'}
                                _focus={{ outline: 'none' }}>
                                <BreakdownContent
                                    breakdown={breakdown}
                                    darkMode={darkMode}
                                />
                            </PopoverContent>
                        </Portal>
                    </Popover>
                );
            })}
        </SimpleGrid>
    );
};

export default SummaryTiles;
